import styled, { css } from "styled-components";

import { CURSORS, PNG } from "@ryde/assets";
import { DATA_E2E_KEY, type IStyledAttributes } from "@ryde/shared";
import { greyMui, rydeColors, ScreenDepth } from "@ryde/theme";

import { MAP_VEHICLE_TRACKING_POPUP_CLASS } from "../../map-tooltip/map-tooltip.constants";
import { StyledMapContainer } from "../map-styled";
import { MAP_CONTROL_STACK } from "../map.constants";
import { STATION_DOT_STROKE_WIDTH_PX } from "./maplibre-marker.constants";
import {
  MAPLIBRE_POLYGON_TOOLTIP_BACKGROUND,
  MAPLIBRE_POLYGON_TOOLTIP_CLASS,
} from "./maplibre-polygons-layer.constants";
import { WALKING_ROUTE_POPUP_CLASS } from "./maplibre-walking-route.constants";

/**
 * Chrome shared by EVERY MapLibre map: canvas sizing, HTML-marker sizing, and popup styling matched to
 * Leaflet's. Route-specific styles (vehicle marker, station dot, incidents) extend this in
 * `route-block-maplibre/parts/route-block.maplibre.styled.ts`.
 *
 * Built on the shared `StyledMapContainer` so both engines keep the same outer map chrome.
 */
export const StyledMapLibreMapHost = styled(StyledMapContainer)<{
  $isPlaceInfoShown?: boolean;
  $isPickingPlace?: boolean;
}>`
  /* The place-info switch is one more group in MapLibre's stack; see MAP_CONTROL_STACK. */
  ${({ $isPlaceInfoShown }) =>
    $isPlaceInfoShown &&
    css`
      ${MAP_CONTROL_STACK.extraHeightVar}: ${
        MAP_CONTROL_STACK.buttonSizePx + MAP_CONTROL_STACK.groupGapPx
      }px;
    `}

  /*
   * Place info is on: the bare map picks a point. A drag (:active) keeps MapLibre's grabbing hand. Tools
   * write the canvas cursor inline (a pointer over a hoverable layer), and only !important outranks that.
   */
  ${({ $isPickingPlace }) =>
    $isPickingPlace &&
    css`
      &
        .maplibregl-canvas-container.maplibregl-interactive:not(:active)
        > .maplibregl-canvas {
        cursor: ${CURSORS.CROSSHAIR_CURSOR_URL} !important;
      }
    `}

  & .maplibregl-popup.maplibre-place-info-popup {
    z-index: ${ScreenDepth.MAP_MAPLIBRE_PLACE_INFO_POPUP};
  }

  /*
   * Intrinsic-size markers: MapLibre HTML markers stretch without an explicit size
   * (classic missing-size / flex stretch → smeared oval pins).
   */
  & .maplibregl-marker {
    direction: ltr /* @noflip */;
    width: max-content !important;
    height: max-content !important;
    max-width: none !important;
  }

  /*
   * Leaflet pin tip (::after) is drawn below the icon box and breaks MapLibre
   * bottom-anchor alignment; SVG pins already include the tip in the path.
   */
  & .map-marker-icon::after,
  & .map-marker-simple-icon::after {
    content: none !important;
    display: none !important;
    border: none !important;
  }

  & .maplibregl-map {
    width: 100%;
    height: 100%;
    position: absolute;
    inset: 0;
  }

  /*
   * Zero-size root for a tip-anchored station pin: the marker sits exactly on the coordinate and the pin
   * is drawn out of it, so a center-anchored marker puts the tip — not the pin's middle — on the point.
   */
  & .maplibre-station-marker {
    cursor: pointer;
    box-sizing: border-box;
    width: 0;
    height: 0;
    position: relative;
    overflow: visible;
    line-height: 0;
    flex-shrink: 0;
  }

  /*
   * Keyboard focus has to be drawn on the pin, not on its root: the root is a zero-size anchor point, so
   * an outline there would have nothing to draw around.
   */
  & .maplibre-station-marker:focus-visible {
    outline: none;
  }

  & .maplibre-station-marker:focus-visible .maplibre-station-pin-body {
    outline: 2px solid ${rydeColors.black};
    outline-offset: 2px;
    border-radius: 4px;
  }

  & .maplibre-station-marker > .maplibre-station-dot {
    position: absolute;
    left: 0;
    top: 0;
    transform: translate(-50%, -50%);
  }

  & .maplibre-station-dot {
    border-radius: 50%;
    border: ${STATION_DOT_STROKE_WIDTH_PX}px solid ${rydeColors.white};
    box-sizing: border-box;
  }

  /*
   * Leaflet's default popup chrome. MapLibre's own is a near-square 3px corner with no shadow, which read
   * as a flat box next to every other card in the app.
   */
  &
    .maplibregl-popup:not(.${MAP_VEHICLE_TRACKING_POPUP_CLASS})
    .maplibregl-popup-content {
    /*
     * Leaflet's effective default: 13px/24px/13px/20px of content margin plus the wrapper's 1px padding.
     * The inline-end stays at 28px instead of 25 because our close button is inset 6px and 22px wide, so
     * its box reaches 28px in — Leaflet's sat flush at 0 and cleared with 24.
     */
    padding: 14px 28px 14px 21px;
    border-radius: 12px;
    box-shadow: 0 3px 14px rgba(0, 0, 0, 0.4);

    /*
     * Leaflet's popup typography, restored. MapLibre puts a 12px/20px font shorthand on the map, and that
     * sets an ABSOLUTE line-height which every popup then inherits — a rule setting only its own
     * font-size to 12px still gets 20px of leading. Leaflet's popups were 13px/1.3, so every
     * popup laid out against them came out taller here: the station chips grew from ~26px to 30px and
     * stopped reading as pills against their fixed 10px radius.
     */
    font-size: 13px;
    line-height: 1.3;
  }

  /*
   * The tail, joined to the card and carrying its own shadow. Three things have to hold at once, and
   * getting any of them wrong draws a visible line across the join:
   *
   * 1. Positioning the tip. MapLibre gives it a z-index of 1 but leaves it statically positioned, where
   *    z-index does nothing. The card IS positioned, so it painted on top and laid its own shadow across
   *    the tail as a grey band.
   * 2. Nudging it up 1px, so it tucks under the card's bottom edge and no hairline can open at the seam.
   * 3. The shadow's OFFSET must exceed its BLUR. A drop-shadow follows the triangle's alpha, so with a
   *    blur wider than the offset it reaches back above the tail's top edge and paints a line onto the
   *    card. At 3px down with 2px of blur it can only ever start 1px below that edge, where the triangle
   *    is still 18 of its 20px wide and hides it.
   */
  &
    .maplibregl-popup:not(.${MAP_VEHICLE_TRACKING_POPUP_CLASS})
    .maplibregl-popup-tip {
    position: relative;
    top: -1px;
    filter: drop-shadow(0 3px 2px rgba(0, 0, 0, 0.18));
  }

  /* Leaflet default close sits inset; MapLibre defaults to flush 0,0. */
  &
    .maplibregl-popup:not(.${MAP_VEHICLE_TRACKING_POPUP_CLASS})
    .maplibregl-popup-close-button {
    top: 6px;
    inset-inline-end: 6px;
    inset-inline-start: auto;
    width: 22px;
    height: 22px;
    padding: 0;
    font-size: 18px;
    line-height: 22px;
    color: ${greyMui._600};
    border-radius: 4px;
    background: transparent;
    border: none;

    &:hover {
      color: ${rydeColors.black};
      background: ${greyMui._100};
    }
  }

  /*
   * A GPS trail point's hover tooltip: a compact card, NOT the padded popup above. It carries no close
   * button, so the room that rule reserves for one reads as a lopsided box with the label adrift in it.
   *
   * Shared rather than per-screen because the card belongs to the trail itself, and every map that
   * draws one renders this same class: the route block and the deep ride investigation both do.
   *
   * On Leaflet the box IS the .custom-tracking-tooltip node; on MapLibre that node is the popup
   * container and the box is .maplibregl-popup-content, so reset the container and paint the content.
   * '&&' outranks the generic popup-content rule above, which is otherwise one class more specific.
   */
  && .maplibregl-popup.custom-tracking-tooltip {
    /* Below station/vehicle popups — mirrors Leaflet's tooltipPane < popupPane. */
    z-index: ${ScreenDepth.MAP_MAPLIBRE_TRAIL_TOOLTIP};
    background: transparent;
    padding: 0;
    border-radius: 0;
    box-shadow: none;
  }

  && .maplibregl-popup.custom-tracking-tooltip .maplibregl-popup-content {
    padding: 4px 10px;
    border-radius: 6px;
    background: ${rydeColors.white};
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.5);
  }

  /*
   * The walking route's hover card, which is bordered where a plain popup is not. It lives here rather than
   * on each screen because the card belongs to a shared component: on Leaflet this was a '.leaflet-tooltip'
   * rule that every screen mounting a walk had to declare for itself.
   *
   * '&&' outranks the generic popup-content rule above, which is otherwise one class more specific.
   */
  && .maplibregl-popup.${WALKING_ROUTE_POPUP_CLASS} .maplibregl-popup-content {
    padding: 0 6px;
    border: 1px solid ${rydeColors.midGrey};
    border-radius: 4px;
  }

  /*
   * ...and its arrow, so card and arrow read as one shape.
   *
   * The tip is a CSS triangle (a 0x0 box with 10px borders) and cannot take a border of its own. Two
   * stacked triangles give it one: the tip is painted in the border colour and an identical white triangle
   * is laid over it 2px higher. That leaves ~1px of grey down the two diagonals — the outline — while the
   * white triangle's wider top edge rides over and hides the 20px of the card's bottom border behind it,
   * which would otherwise draw a line straight across the join.
   *
   * ::after is positioned from the tip's 0x0 content box, so -10px/-10px puts it back on the tip's own
   * border box and the extra -2px is the overlap.
   */
  && .maplibregl-popup.${WALKING_ROUTE_POPUP_CLASS} .maplibregl-popup-tip {
    position: relative;
    border-top-color: ${rydeColors.midGrey};
    /* This tail is outlined by the two triangles below, not shadowed like a plain popup's. */
    filter: none;
  }

  &&
    .maplibregl-popup.${WALKING_ROUTE_POPUP_CLASS}
    .maplibregl-popup-tip::after {
    content: "";
    position: absolute;
    top: -12px;
    left: -10px;
    width: 0;
    height: 0;
    border: 10px solid transparent;
    border-bottom: none;
    border-top-color: ${rydeColors.white};
  }

  /*
   * A polygon's hover label. The areas map drew this as a dark pill rather than the white card every other
   * popup gets, and it names the area with its id — kept LTR so that "(#12)" reads the same in Hebrew.
   */
  &&
    .maplibregl-popup.${MAPLIBRE_POLYGON_TOOLTIP_CLASS}
    .maplibregl-popup-content {
    direction: ltr /* @noflip */;
    padding: 4px 10px;
    border: none;
    border-radius: 6px;
    color: ${rydeColors.white};
    background-color: ${MAPLIBRE_POLYGON_TOOLTIP_BACKGROUND};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
    font-size: 13px;
    font-weight: 600;
    line-height: 1.3;
  }

  && .maplibregl-popup.${MAPLIBRE_POLYGON_TOOLTIP_CLASS} .maplibregl-popup-tip {
    border-top-color: ${MAPLIBRE_POLYGON_TOOLTIP_BACKGROUND};
    /* The pill carries its own shadow; the plain popup's tail shadow would show as a grey smudge here. */
    filter: none;
  }
`;

export const MapLibreWebGLUnavailableStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "maplibre-webgl2-unavailable",
  },
)`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: url(${PNG.GlobeBgPNG}) no-repeat;
  background-size: cover;

  & .maplibre-webgl-unavailable-message {
    max-width: 360px;
    margin: 16px;
    padding: 12px 16px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.92);
    color: ${rydeColors.black};
    text-align: center;
    font-size: 14px;
    line-height: 1.4;
  }
`;
